import { MenuItem } from '@app/components/various/Menu';
import { cn } from '@app/utils/cn';
import { isDefined } from '@app/utils/is';
import { Selects } from '@typings';
import React from 'react';

import { useSelectContext } from '../context/SelectContext';

interface Props {
  children: React.ReactNode;
  value: Selects.NonNullableValue;
  description?: string;
  selectedLabel?: string;
  index?: number;
  onClick?: () => void;
  maskData?: boolean;
}

export const Option = ({ children, value, description, index, onClick, maskData = false }: React.PropsWithChildren<Props>) => {
  const ref = React.useRef<HTMLButtonElement>(null);
  const { focusedOptionPosition, hoveredOptionPosition, size, selection, optionsId, hoverOption, handleSelect, isMaxSelectedReached } =
    useSelectContext();
  const isFocused = focusedOptionPosition === index;
  const isHovered = hoveredOptionPosition === index;
  const isSelected = [selection].flat().includes(value);
  const isLastSelected = [selection].flat().slice(-1)[0] === value;
  const shouldHoverOption = isLastSelected && !isDefined(hoveredOptionPosition);

  React.useEffect(() => {
    if (isHovered) {
      ref.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [isHovered]);

  React.useEffect(() => {
    if (shouldHoverOption && isDefined(index)) {
      hoverOption(index);
    }
  }, [hoverOption, index, shouldHoverOption]);

  const handleClick = () => {
    handleSelect(value);
    onClick?.();
  };

  const handleMouseMove = () => {
    if (isDefined(index)) {
      hoverOption(index);
    }
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
  };

  return (
    <MenuItem
      id={`${optionsId}-${value}`}
      ref={ref}
      isSelected={isSelected}
      isFocused={isFocused}
      role="option"
      size={size}
      description={description}
      // eslint-disable-next-line sort-keys-fix/sort-keys-fix
      className={cn({ 'bg-neutral-96': isHovered, 'bg-neutral-90': isSelected && isHovered })}
      onMouseMove={handleMouseMove}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      maskData={maskData}
      isDisabled={isMaxSelectedReached && !isSelected}
    >
      {children}
    </MenuItem>
  );
};
